import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "../constants";
import { Reveal } from "./motion";

const cards = [
  {
    title: "SOUL IN LOVE",
    subtitle: "Spiritual work rooted in love, sound, and inner knowing.",
    copy: "Soul in Love is the contemplative and spiritual expression of The SEAL Collective — encompassing channeled work, vocal frequencies, sound experiences, teachings, meditations, and spaces for deeper listening.",
    cta: "Explore Soul in Love",
    path: "/soul-in-love",
    span: "lg:col-span-7",
    testId: "card-soul-in-love",
  },
  {
    title: "AMBER ELLIS",
    subtitle: "Voice as art. Voice as expression. Voice as instrument.",
    copy: "Recording artist, professional vocalist, voice talent, and creator Amber Ellis brings decades of performance, communication, teaching, and vocal experience into music, narration, sound, and immersive vocal work.",
    cta: "Explore Amber Ellis",
    path: "/amber-ellis",
    span: "lg:col-span-5",
    testId: "card-amber-ellis",
  },
  {
    title: "TUNDALA",
    subtitle: "A companion for sound, voice, and the states between.",
    copy: "Tundala is an emerging sound-based companion — chants, tones, guided sound, and vocal experiences that help you shift your state and meet the next moment well. Born from the living sound library of Soul in Love.",
    cta: "Discover Tundala",
    path: "/tundala",
    span: "lg:col-span-5",
    testId: "card-tundala",
  },
  {
    title: "TONAL INTENTIONS™",
    subtitle: "Sound for the state you intend to enter.",
    copy: "A modern workplace wellness and human-performance experience: short, intentional sound-based resets for the moments between the moments. Simple enough for individuals, powerful enough for groups of any size.",
    cta: "Explore Tonal Intentions",
    scrollTo: "tonal-intentions",
    span: "lg:col-span-7",
    testId: "card-tonal-intentions",
    badge: "For the Workplace",
    tags: ["Workplace Wellness", "Human Performance", "Sales Teams", "Any Team Size"],
  },
];

const Collective = () => (
  <section
    id="the-collective"
    data-testid="collective-section"
    className="relative mx-auto max-w-7xl px-6 py-32 lg:px-12"
  >
    <Reveal>
      <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
        Four Expressions, One Center
      </p>
      <h2
        data-testid="collective-headline"
        className="font-serif-display max-w-3xl text-4xl font-light tracking-tight text-[#16130e] sm:text-5xl"
      >
        EXPLORE THE COLLECTIVE
      </h2>
    </Reveal>

    <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12">
      {cards.map((c, i) => (
        <Reveal key={c.title} delay={i * 0.08} className={c.span}>
          <motion.article
            data-testid={c.testId}
            whileHover={{ y: -6 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="group relative flex h-full flex-col justify-between overflow-hidden border hairline bg-white/60 p-10 backdrop-blur-sm transition-colors duration-700 hover:border-[#b8912e]/50 hover:shadow-[0_20px_60px_rgba(22,19,14,0.08)] lg:p-12"
          >
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.14)_0%,transparent_70%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
            <div>
              <div className="mb-8 flex items-baseline justify-between">
                {c.badge ? (
                  <span
                    data-testid="org-app-in-build-badge"
                    className="border border-[#b8912e]/50 bg-[#b8912e]/10 px-3 py-1 text-[0.55rem] font-light uppercase tracking-[0.25em] text-[#8a6d1f]"
                  >
                    {c.badge}
                  </span>
                ) : (
                  <span />
                )}
                <span className="h-px w-16 bg-gradient-to-r from-[#b8912e]/60 to-transparent" />
              </div>
              <h3 className="font-serif-display text-3xl font-light tracking-wide text-[#16130e]">{c.title}</h3>
              <p className="mt-3 text-sm font-light italic text-[#8a6d1f]">{c.subtitle}</p>
              <p className="mt-6 text-sm font-extralight leading-relaxed text-black/55">{c.copy}</p>
              {c.tags && (
                <div data-testid="organizations-tags" className="mt-8 flex flex-wrap gap-2">
                  {c.tags.map((t) => (
                    <span
                      key={t}
                      className="border hairline px-3 py-1.5 text-[0.6rem] font-light uppercase tracking-[0.2em] text-black/50"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {c.scrollTo ? (
              <button
                data-testid={`${c.testId}-button`}
                onClick={() => scrollToId(c.scrollTo)}
                className="btn-outline mt-10 w-fit !text-[0.6rem]"
              >
                {c.cta}
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </button>
            ) : (
              <Link
                data-testid={`${c.testId}-button`}
                to={c.path}
                className="btn-outline mt-10 w-fit !text-[0.6rem]"
              >
                {c.cta}
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </Link>
            )}
          </motion.article>
        </Reveal>
      ))}
    </div>
  </section>
);

export default Collective;
